import { useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCanBookPatient } from '../../store/permissions/permissionsSlice';
import ProfileIcon from '../../assets/icons/profile.svg?react';
import TrashIcon from '../../assets/icons/trash.svg?react';
import styles from './PatientCard.module.css';

interface PatientCardProps {
  name: string;
  practitioners: string;
  tag?: string;
  deleted?: boolean;
  // Renders the row as an open, fillable seat (dashed lane) instead of a
  // patient: the whole body triggers the add-patient flow via onClick.
  isAvailableSeat?: boolean;
  onClick?: () => void;
  onRemove?: (anchorRef: RefObject<HTMLButtonElement | null>) => void;
}

export const PatientCard = ({
  name,
  practitioners,
  tag,
  deleted,
  isAvailableSeat,
  onClick,
  onRemove
}: PatientCardProps) => {
  const { t } = useTranslation();
  const canBookPatient = useAppSelector(selectCanBookPatient);
  const trashRef = useRef<HTMLButtonElement>(null);

  if (isAvailableSeat) {
    return (
      <div className={`${styles.card} ${styles.availableSeatCard}`}>
        <button
          className={styles.availableSeatBody}
          onClick={onClick}
          disabled={!onClick}
          type="button"
        >
          <div className={styles.cardContent}>
            <div className={styles.topRow}>
              <div className={styles.iconCol}>
                <ProfileIcon />
              </div>
              <span className={styles.availableSeatName}>{name}</span>
            </div>
            {onClick && (
              <div className={styles.bottomRow}>
                <span className={styles.assignHint}>+ {t('add-patient')}</span>
              </div>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardContent}>
        <div className={styles.topRow}>
          <div className={styles.iconCol}>
            <ProfileIcon />
          </div>
          {onClick ? (
            <button className={styles.nameButton} onClick={onClick}>
              {name}
            </button>
          ) : (
            <span className={styles.value}>{name}</span>
          )}
          {tag && <span className={styles.tag}>{tag}</span>}
        </div>
        <div className={styles.bottomRow}>
          <span className={styles.label}>{t('practitioners-label')}</span>{' '}
          <span className={styles.value}>{practitioners}</span>
        </div>
      </div>
      <div className={styles.actions}>
        {canBookPatient && !deleted && onRemove && (
          <button
            ref={trashRef}
            className={styles.deleteButton}
            aria-label="Delete"
            onClick={(e) => { e.stopPropagation(); onRemove?.(trashRef); }}
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
};
